const libFableServiceProviderBase = require('fable-serviceproviderbase');

const dayJS = require('dayjs');
/**
 * Headlight LegacyQRCodeGenerator fable service
 */
class HeadlightQRCodeHash extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable - The fable instance
	 * @param {any} [pOptions] - The options for the service
	 * @param {any} [pServiceHash] - The hash for the service instance
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'HeadlightQRCodeHash';

		this.generatedGUIDs = [];
	}

	/**
	* A generic method to generate a GUID (unique id) string value out of alphanumeric
	* characters. The default length is 10 characters long but can be customized.
	* @param {number} [prefix] - an optional prefix, used to make the scope more clear
	* @param {number} [length] - the number of characters in the return string
	* @return {string} - a generated id string
	*/
	// TODO: Should we replace this with a Pict/Fable method that does the same thing but better? Unsure if it exists, but probably does.
	_generateGUID(prefix, length)
	{
		const alphanumericString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
		// use a custom length?
		length = length ? length : 10;
		// make a string id of random characters
		let guid = '';
		for (let i = 0; i < length; i++)
		{
			guid += alphanumericString.charAt(Math.floor(Math.random() * alphanumericString.length));
		}
		// if we have an optional prefix, set that now
		guid = prefix ? prefix + guid : guid;
		return guid;
	};

	/**
	 * Generates QR Codes in the "new" format, takes a project id and user id.
	 * @param {Number} projectId 
	 * @param {Number} userId 
	 * @returns {String}
	 */
	_generateHLQRCode(projectId, userId) {
		let guid = null, attempts = 0;
		do {
			attempts++;
	
			if(attempts > 100) {
				console.error('Failed to generate unique guid. Too many attempts. Returning random string.');
				break;
			}
	
			// simple left pad
			const lpad = (v, len, char = '') => {
				let r = String(v);
				while(r.length < len) r = char + r;
				return r;
			}
	
			let calendarDayMap = ['0','1','2','3','4','5','6','7','8','9','A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V'];
			let date = new Date();
			let year = lpad(date.getFullYear() % 100, 2, '0');
			let month = calendarDayMap[date.getMonth()+1];
			let day = calendarDayMap[date.getDate()];
			let hour = lpad(date.getHours() % 12, 2, '0');
			let min = lpad(date.getMinutes(), 2, '0');
			let sec = lpad(date.getSeconds(), 2, '0');
			let ms = lpad(date.getMilliseconds().toString(36), 2, '0');
	
			const projectCode = projectId ? parseInt(projectId).toString(36) : '0';
	
			const userCode = userId ? parseInt(userId).toString(36) : '0';
	
			// what we want for this pattern
			guid = `${projectCode.toUpperCase()}-${userCode.toUpperCase()}-${year}${month}${day}${hour}${min}${sec}${ms}`;
		} while(this.generatedGUIDs.includes(guid));
	
		this.generatedGUIDs.push(guid);
	
		return guid;
	}
	
	/**
	* This method is used to generate a QR code string for a given GUID in the legacy "date/hash" format. (LADOTD, SiteManager, etc)
	*
	* QR code should contain the User ID as well as the Date, to aid in finding Samples via the SiteManager user interface
	* This is what needs to happen:
	* QRCode: CMS_UID + year (2 chars) + month (1 char) + date (1 char) + hour - min - sec (6 chars)
	* where CMS_UID = user
	* and date = a letter
	* So, e.g. my sample id is 0x591745201D11585798 (0x59174598.20.1.D.11.58.57)
	* my user = 0x59174598
	* year = 20
	* month = 1
	* day = D (A- 10th, B - 11th, C - 12th, D - 13th)
	* hour-min-sec = 115857
	* @param {string} tmpGUIDString - The GUID to generate the QR code for
	* @return {string} - The QR code string
	*/
	_generateLegacyGUID(tmpGUIDString)
	{
		tmpGUIDString = tmpGUIDString ? tmpGUIDString : '';
		// there's a limit to the length of the QR code, so we need to truncate the GUID at 10 characters
		if (tmpGUIDString.length > 10)
		{
			tmpGUIDString = tmpGUIDString.substring(0, 10);
		}
		const calendarDayMap = [ '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V' ];
		const time = new Date().toISOString();
		// Match the materials app's SiteManager generator (generateDateID): format the components in the
		// PROJECT timezone and on a 12-hour clock ('hh'). In forms the fable Dates service is configured with
		// the project TZ (with fallback), so formatDate applies it; only off-form does this fall back to the
		// bare dayjs (runtime-local).
		const tmpFormat = (pFormat) => (this.fable && this.fable.Dates && typeof this.fable.Dates.formatDate === 'function') ? (this.fable.Dates.formatDate(time, pFormat) || '') : dayJS(time).format(pFormat);
		const year = tmpFormat('YY');
		const hour = tmpFormat('hh');
		const min = tmpFormat('mm');
		const sec = tmpFormat('ss');
		let month = tmpFormat('M');
		// use the day as an index for calendarDayMap above
		let day = tmpFormat('D');
		month = calendarDayMap[month];
		day = calendarDayMap[day];
		// what we want for this pattern
		return tmpGUIDString + year + month + day + hour + min + sec;
	}

	/**
	 * This handles the generation of the QR code hash string based on the options passed in
	 * @param {object} [pOptions] - The options for the hash generation
	 * @return {string} - The hash string
	 */
	generateQRCodeHash(pOptions)
	{
		if (pOptions && typeof pOptions !== 'object')
		{
			throw new Error('generateQRCodeHash() was expecting an object of options, but received a non-object value.');
		}
		pOptions = pOptions || {};
		// generate a LegacyFormat QR code hash if the option is set
		if (pOptions.LegacyFormat && pOptions.GUIDPrefix)
		{
			return this._generateLegacyGUID(pOptions.GUIDPrefix);
		}
		if (pOptions.LegacyFormat && !pOptions.GUIDPrefix)
		{
			throw new Error('generateQRCodeHash() was expecting a GUIDPrefix value for the LegacyFormat option, but received none.');
		}
		return this._generateHLQRCode(pOptions.IDProject, pOptions.IDUser);
		
	}
}

module.exports = HeadlightQRCodeHash;
